"use client";

import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@/app/lib/hooks";
import { addNewsletter } from "@/app/lib/reducers/newsletterReducer";

/**
 * Newsletter section — two-column compact layout:
 *   - Left: eyebrow + display title with italic green accent + subtitle
 *   - Right: pill-shaped email input with the green "S'inscrire" button
 *     embedded inside the pill, plus 3 trust checkmarks underneath
 *
 * Wires the existing `addNewsletter(email, language, origin)` action — no
 * new backend, no new endpoint.
 */
const SectionNewsletter: FC<{ className?: string }> = ({ className = "" }) => {
  const { t } = useTranslation("guydeez");
  const dispatch = useAppDispatch();
  const { loading, error, success } = useAppSelector((s) => s.newsletter);
  const language = useAppSelector((s) => s.language.selectedLanguage || "fr_FR");

  const [email, setEmail] = useState("");
  const [showOk, setShowOk] = useState(false);
  const [showErr, setShowErr] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
      setValidationError(
        t("homepage_newsletter_invalid_email", {
          defaultValue: "Adresse email invalide.",
        })
      );
      return;
    }
    setValidationError(null);
    const origin =
      typeof window !== "undefined" ? window.location.href : "website";
    dispatch(addNewsletter(trimmed, language, origin));
  };

  useEffect(() => {
    if (success) {
      setShowOk(true);
      setEmail("");
      const id = setTimeout(() => setShowOk(false), 4000);
      return () => clearTimeout(id);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      setShowErr(true);
      const id = setTimeout(() => setShowErr(false), 4000);
      return () => clearTimeout(id);
    }
  }, [error]);

  return (
    <section
      className={`gdx ${className}`}
      style={{
        background: "var(--g-cream)",
        // Vertical padding only — the horizontal margins are owned by the
        // inner wrapper below so they share the SAME math as the
        // /continents and /continent/[slug] page wrappers:
        // content area = min(viewport, maxWidth) − 2 × horizontal padding.
        padding: "clamp(48px, 6vw, 80px) 0",
      }}
    >
      <div
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          // Horizontal padding lives INSIDE the maxWidth box (same pattern
          // as the rest of the site), so the form / text aligns pixel-
          // perfect with the cards above at every viewport.
          padding: "0 clamp(20px, 4vw, 56px)",
          display: "grid",
          // `auto-fit minmax(360px,1fr)` stacks the two columns into one as
          // soon as each would fall under 360px wide — i.e. anywhere below
          // ~760px including narrow tablets — so the title doesn't get
          // squeezed into 4 lines like in the bug report.
          gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
          gap: "clamp(28px, 4vw, 80px)",
          alignItems: "center",
        }}
      >
        {/* Left: copy */}
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "var(--g-coral)",
              marginBottom: 12,
            }}
          >
            {t("homepage_newsletter_eyebrow", { defaultValue: "Carnet de voyage" })}
          </div>
          <h2
            style={{
              fontFamily: "var(--g-font-display)",
              // Fluid font-size from 26px (narrow) to 36px (>=900px) keeps
              // the headline on 2 lines max in every viewport we ship.
              fontSize: "clamp(26px, 3.2vw, 36px)",
              fontWeight: 800,
              lineHeight: 1.15,
              color: "var(--g-burgundy)",
              letterSpacing: "-0.02em",
              marginBottom: 14,
            }}
          >
            {t("homepage_newsletter_title_pre", { defaultValue: "Recevez nos" })}{" "}
            <span
              style={{
                color: "var(--g-green-deep)",
                fontStyle: "italic",
                fontWeight: 600,
              }}
            >
              {t("homepage_newsletter_title_mid", { defaultValue: "pépites locales" })}
            </span>{" "}
            {t("homepage_newsletter_title_post", { defaultValue: "chaque mois" })}
          </h2>
          <p
            style={{
              fontSize: 15,
              color: "var(--g-ink-500)",
              lineHeight: 1.55,
              // Let it flow to the column width when stacked — capping at
              // 480px when wide enough to keep the line length comfortable.
              maxWidth: "min(480px, 100%)",
            }}
          >
            {t("homepage_newsletter_subtitle", {
              defaultValue:
                "Tours en avant-première, bons plans destinations et histoires de nos guides.",
            })}
          </p>
        </div>

        {/* Right: form */}
        <div>
          <form
            onSubmit={handleSubmit}
            style={{
              display: "flex",
              alignItems: "center",
              padding: 6,
              background: "white",
              borderRadius: 999,
              boxShadow: "0 6px 18px -8px rgba(91,31,58,0.15)",
              border: "1px solid var(--g-line)",
            }}
          >
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("homepage_newsletter_placeholder", {
                defaultValue: "votre@email.com",
              })}
              // The Tailwind classes are required to override the focus ring
              // that `@tailwindcss/forms` injects via box-shadow + outline on
              // every input — inline `outline: none` alone doesn't cover it.
              className="focus:outline-none focus:ring-0 focus:border-transparent focus:shadow-none"
              style={{
                // `minWidth: 0` lets the flex item shrink below its content
                // size, so the pill stays single-line on narrow viewports
                // instead of forcing the form to overflow horizontally.
                flex: 1,
                minWidth: 0,
                padding: "12px 18px",
                background: "transparent",
                border: 0,
                outline: "none",
                boxShadow: "none",
                fontSize: 15,
                color: "var(--g-ink-900)",
              }}
            />
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: "12px 24px",
                // Brand-adjusted green: contrast 5.36 vs white text (AA pass)
                // — the original `--g-green` (#4FBF6E) fails Lighthouse a11y.
                background: loading ? "var(--g-green-ink)" : "var(--g-green-ada)",
                color: "white",
                border: 0,
                borderRadius: 999,
                fontSize: 14,
                fontWeight: 700,
                cursor: loading ? "wait" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                whiteSpace: "nowrap",
                boxShadow: "0 6px 14px -4px rgba(31,122,69,0.42)",
              }}
            >
              {t("homepage_newsletter_cta", { defaultValue: "S'inscrire" })}
              <ArrowIcon />
            </button>
          </form>

          {/* Trust row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 22,
              marginTop: 14,
              paddingLeft: 8,
              fontSize: 13,
              color: "var(--g-ink-500)",
              flexWrap: "wrap",
            }}
          >
            <Trust label={t("homepage_newsletter_no_spam", { defaultValue: "Sans spam" })} />
            <Trust label={t("homepage_newsletter_unsubscribe", { defaultValue: "Désinscription en 1 clic" })} />
            <Trust label={t("homepage_newsletter_gdpr", { defaultValue: "RGPD" })} />
          </div>

          {showOk && (
            <p
              style={{
                marginTop: 12,
                fontSize: 13,
                color: "var(--g-green-deep)",
                fontWeight: 600,
              }}
            >
              {t("homepage_newsletter_success", {
                defaultValue: "Merci ! Vous êtes inscrit·e.",
              })}
            </p>
          )}
          {validationError && (
            <p
              style={{
                marginTop: 12,
                fontSize: 13,
                color: "var(--g-coral)",
                fontWeight: 600,
              }}
            >
              {validationError}
            </p>
          )}
          {showErr && error && !validationError && (
            <p
              style={{
                marginTop: 12,
                fontSize: 13,
                color: "var(--g-coral)",
                fontWeight: 600,
              }}
            >
              {error}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

const Trust: FC<{ label: string }> = ({ label }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
    <CheckIcon /> {label}
  </span>
);

const CheckIcon = () => (
  <svg
    width={14}
    height={14}
    viewBox="0 0 24 24"
    fill="none"
    stroke="var(--g-green-deep)"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M5 13l4 4L19 7" />
  </svg>
);

// Email validation regex — pragmatic (not RFC-complete): rejects spaces,
// requires `local@domain.tld` shape with a real TLD chunk. Server-side
// validation still happens in `addNewsletter`, this is purely UX.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (s: string) => EMAIL_RE.test(s);

const ArrowIcon = () => (
  <svg
    width={14}
    height={14}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    aria-hidden
  >
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

export default SectionNewsletter;
